// src/components/admin/flows/components/WorkflowCard.js
import React, { useState } from 'react';
import {
    GitBranch, MoreVertical, Edit3, Copy, Pause, Play, Trash2, Archive, Rocket
} from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import StatusBadge from './StatusBadge';
import StepIcon from './StepIcon';

const TRIGGER_LABELS = {
    employee_added: 'Employee Added',
    employee_onboarded: 'Employee Onboarded',
    employee_joined: 'Employee Onboarded',
    employee_exit_initiated: 'Exit Initiated',
    leave_requested: 'Leave Requested',
    attendance_regularization: 'Attendance Issue',
    probation_ending: 'Probation Ending',
    birthday: 'Birthday',
    work_anniversary: 'Work Anniversary',
    manual: 'Manual Trigger',
    scheduled: 'Scheduled',
};

const TYPE_COLORS = {
    onboarding: 'border-l-green-500',
    offboarding: 'border-l-red-500',
    approval_chain: 'border-l-purple-500',
    task_automation: 'border-l-blue-500',
    custom: 'border-l-gray-500',
};

const WorkflowCard = ({ workflow, onEdit, onDuplicate, onToggleStatus, onDelete, onRun }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [showMenu, setShowMenu] = useState(false);

    // Derive trigger from the trigger step's config (more reliable than top-level trigger field)
    const triggerStep = workflow.steps?.find(s => s.type === 'trigger');
    const triggerEvent = triggerStep?.config?.event || workflow.trigger?.event;
    const triggerLabel = TRIGGER_LABELS[triggerEvent] || triggerEvent?.replace(/_/g, ' ') || 'Unknown';
    const typeColor = TYPE_COLORS[workflow.type] || TYPE_COLORS.custom;
    const stepCount = workflow.steps?.length || 0;

    return (
        <div
            className={`relative rounded-xl border-l-4 ${typeColor} ${
                isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
            } shadow-sm hover:shadow-md transition-shadow cursor-pointer`}
            onClick={() => onEdit(workflow)}
        >
            <div className="p-4">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <GitBranch className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                        <h3 className={`font-semibold text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {workflow.name}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusBadge status={workflow.status} />
                        <div className="relative">
                            <button
                                onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                                className={`p-1 rounded hover:${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}
                            >
                                <MoreVertical className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                            </button>
                            {showMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setShowMenu(false); }} />
                                    <div className={`absolute right-0 top-8 z-20 w-44 rounded-lg shadow-lg border ${
                                        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                                    }`}>
                                        <button
                                            onPointerDown={(e) => { e.stopPropagation(); }}
                                            onClick={(e) => { e.stopPropagation(); onEdit(workflow); setShowMenu(false); }}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                        >
                                            <Edit3 className="w-3.5 h-3.5" /> Edit
                                        </button>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDuplicate(workflow.id); }}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50'}`}
                                        >
                                            <Copy className="w-3.5 h-3.5" /> Duplicate
                                        </button>
                                        {workflow.status === 'active' && onRun && triggerEvent === 'manual' && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setShowMenu(false); onRun(workflow.id); }}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? 'text-blue-400 hover:bg-gray-700' : 'text-blue-600 hover:bg-gray-50'}`}
                                            >
                                                <Rocket className="w-3.5 h-3.5" /> Run Now
                                            </button>
                                        )}
                                        {workflow.status === 'active' ? (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setShowMenu(false); onToggleStatus(workflow.id, 'paused'); }}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? 'text-amber-400 hover:bg-gray-700' : 'text-amber-600 hover:bg-gray-50'}`}
                                            >
                                                <Pause className="w-3.5 h-3.5" /> Pause
                                            </button>
                                        ) : workflow.status !== 'archived' ? (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setShowMenu(false); onToggleStatus(workflow.id, 'active'); }}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? 'text-green-400 hover:bg-gray-700' : 'text-green-600 hover:bg-gray-50'}`}
                                            >
                                                <Play className="w-3.5 h-3.5" /> Activate
                                            </button>
                                        ) : null}
                                        {workflow.status !== 'archived' && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setShowMenu(false); onToggleStatus(workflow.id, 'archived'); }}
                                                className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-50'}`}
                                            >
                                                <Archive className="w-3.5 h-3.5" /> Archive
                                            </button>
                                        )}
                                        <div className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`} />
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setShowMenu(false); onDelete(workflow.id); }}
                                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-red-500 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" /> Delete
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Description */}
                {workflow.description && (
                    <p className={`text-xs mb-3 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {workflow.description}
                    </p>
                )}

                {/* Meta */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                            {triggerLabel}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {stepCount} step{stepCount !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        {workflow.stats?.runs ?? 0} runs
                    </span>
                </div>
            </div>
        </div>
    );
};

export default WorkflowCard;
